import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  endBefore,
  getFirestore,
  increment,
  limit,
  limitToLast,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../config/firebase";
import {
  generateKeywordsFromString,
  generateUniqueKeywords,
} from "../../js/helpers/keywordHelpers";
import { EMPLOYER_USER, JOB_SEEKER_USER } from "./firebaseService";
import {
  FIRESTORE_APPLICATIONS_COLLECTION,
  FIRESTORE_CANDIDATE_PROFILES_COLLECTION,
  FIRESTORE_CANDIDATE_PROFILES_METRICS_COLLECTION,
  FIRESTORE_COMPANY_PROFILE_COLLECTION,
  FIRESTORE_COOKIE_CONSENT_RECORD_COLLECTION,
  FIRESTORE_COUPONS_COLLECTION,
  FIRESTORE_DRAFT_JOBS_COLLECTION,
  FIRESTORE_ERRORS_COLLECTION,
  FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION,
  FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION,
  FIRESTORE_JOB_POST_ORDER_COLLECTION,
  FIRESTORE_JOBS_COLLECTION,
  FIRESTORE_JOBS_STATUS_LOG_COLLECTION,
  FIRESTORE_PROMOTION_REQUESTS_COLLECTION,
  FIRESTORE_REQUEST_DELETE_ACCOUNTS_COLLECTION,
  FIRESTORE_SAVED_JOBS_COLLECTION,
  FIRESTORE_SAVED_LOCATIONS_SUB_COLLECTION,
  FIRESTORE_USERS_COLLECTION,
  FIRESTORE_VIEWS_COLLECTION,
} from "./firestoreConstants";
import omit from "lodash/omit";
import { format, isAfter, setHours, sub } from "date-fns";
import { setExpiryDate } from "../../features/jobs/helpers/setExpiryDate";
import {
  getFullDate,
  getIsoWeekDate,
  getMonthYearDate,
} from "../../js/helpers/dateHelpers";
import cuid from "cuid";
import {
  ASAP_JOB,
  SIX_MONTHS_JOB,
} from "../../features/candidates/candidateProfilesFormConstants";
import { getAdditionalPromotedDates } from "../../features/employer/helpers/setAdditionalJobData";
import { EMPTY_LOCATION } from "../../features/employer/helpers/postJobFormConstants";
import {
  HR_ALL_MODE,
  HR_HEALTHCARE_MODE,
} from "../../features/modes/constants";
import {
  checkToUpdateMultiLocJobs,
  MULTIPLE_LOCATION_JOB_EMPLOYER_ID,
} from "../../js/helpers/multipleJobLocationHelpers";
import {
  CUSTOM_ALERT_TYPE,
  WEEKLY_ALERT_FREQUENCY,
} from "../../features/candidates/components/JobAlertsPane";

export const INITIAL_SEARCH = "initial";
export const FORWARDS_SEARCH = "forwards";
export const BACKWARDS_SEARCH = "backwards";
export const LIMIT = 30;
const RECENT_LIMIT = 50;
export const RECENT_DAYS = 9;

const db = getFirestore(app);
const auth = getAuth(app);

export function dataFromSnapshot(snapshot) {
  if (!snapshot.exists) return undefined;
  let data = snapshot.data();

  for (const prop in data) {
    if (data.hasOwnProperty(prop)) {
      if (data[prop] instanceof Timestamp) {
        data[prop] = data[prop].toDate();
      }
    }
  }

  return {
    ...omit(data, "keywords"),
    id: snapshot.id,
  };
}
// 14
export function fetchKeywordFilterJobsFromFirestore({
  countryCode,
  areaLevelOne,
  keywordsInput,
  search = INITIAL_SEARCH,
  positionSnapshot = null,
  mode = HR_ALL_MODE,
}) {
  const keywords = generateUniqueKeywords(keywordsInput);
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("keywords", "array-contains-any", keywords),
  ];
  if (mode === HR_HEALTHCARE_MODE) {
    filters.push(where("mode", "==", HR_HEALTHCARE_MODE));
  }

  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 15
export function fetchIndustryFilterJobsFromFirestore(
  countryCode,
  areaLevelOne,
  industry,
  search = INITIAL_SEARCH,
  positionSnapshot = null
) {
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("industry", "==", industry),
  ];
  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 16
export function fetchJobTypeFilterJobsFromFirestore({
  countryCode,
  areaLevelOne,
  jobType,
  search = INITIAL_SEARCH,
  positionSnapshot = null,
  mode = HR_ALL_MODE,
}) {
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("type", "==", jobType),
  ];
  if (mode === HR_HEALTHCARE_MODE) {
    filters.push(where("mode", "==", HR_HEALTHCARE_MODE));
  }

  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 17
export function fetchKeywordAndIndustryFilterJobsFromFirestore(
  countryCode,
  areaLevelOne,
  keywordsInput,
  industry,
  search = INITIAL_SEARCH,
  positionSnapshot = null
) {
  const keywords = generateUniqueKeywords(keywordsInput);
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("keywords", "array-contains-any", keywords),
    where("industry", "==", industry),
  ];
  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 18
export function fetchKeywordAndJobTypeFilterJobsFromFirestore(
  countryCode,
  areaLevelOne,
  keywordsInput,
  jobType,
  search = INITIAL_SEARCH,
  positionSnapshot = null
) {
  const keywords = generateUniqueKeywords(keywordsInput);
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("keywords", "array-contains-any", keywords),
    where("type", "==", jobType),
  ];
  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 19
export function fetchIndustryAndJobTypeFilterJobsFromFirestore(
  countryCode,
  areaLevelOne,
  industry,
  jobType,
  search = INITIAL_SEARCH,
  positionSnapshot = null
) {
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("industry", "==", industry),
    where("type", "==", jobType),
  ];
  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 20
export function fetchKeywordAndIndustryAndJobTypeFilterJobsFromFirestore(
  countryCode,
  areaLevelOne,
  keywordsInput,
  industry,
  jobType,
  search = INITIAL_SEARCH,
  positionSnapshot = null
) {
  const keywords = generateUniqueKeywords(keywordsInput);
  const today = new Date();
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("keywords", "array-contains-any", keywords),
    where("industry", "==", industry),
    where("type", "==", jobType),
  ];
  if (search === INITIAL_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      limit(LIMIT)
    );
  } else if (search === FORWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      startAfter(positionSnapshot),
      limit(LIMIT)
    );
  } else if (search === BACKWARDS_SEARCH) {
    return query(
      collection(db, FIRESTORE_JOBS_COLLECTION),
      ...filters,
      orderBy("dateExpiring", "desc"),
      endBefore(positionSnapshot),
      limitToLast(LIMIT)
    );
  }
}

// 21
export function listenToSponsoredJobsFromFirestore(
  countryCode,
  areaLevelOne,
  mode
) {
  const today = new Date();
  const todayFormatted = getFullDate(today);

  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("isSponsoredPromotion", "==", true),
    where("datesSponsoredFor", "array-contains", todayFormatted),
  ];
  if (mode === HR_HEALTHCARE_MODE) {
    filters.push(where("mode", "==", HR_HEALTHCARE_MODE));
  }

  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    ...filters,
    orderBy("dateExpiring", "desc")
  );
}

export function listenToRecentlyAddedJobsFromFirestore({
  countryCode,
  areaLevelOne,
  days = RECENT_DAYS,
  mode = HR_ALL_MODE,
}) {
  const dateRecently = sub(setHours(new Date(), 0), {
    days: days,
  });
  const filters = [
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("datePublished", ">=", dateRecently),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
  ];
  if (mode === HR_HEALTHCARE_MODE) {
    filters.push(where("mode", "==", HR_HEALTHCARE_MODE));
  }

  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    ...filters,
    orderBy("datePublished", "desc"),
    limit(RECENT_LIMIT)
  );
}

export function listenToPromotionCompanyFeaturedLogosFromFirestore(
  countryCode
) {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("countryCode", "==", countryCode),
    orderBy("dateExpiring", "desc")
  );
}

export function listenToCountrySponsoredJobsFromFirestore(countryCode) {
  const today = new Date();
  const todayFormatted = getFullDate(today);
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("isSponsoredPromotion", "==", true),
    where("datesSponsoredFor", "array-contains", todayFormatted),
    orderBy("dateExpiring", "desc")
  );
}

// 24
export function fetchSimilarFeaturedJobsFromFirestore(
  countryCode,
  areaLevelOne,
  industry,
  jobType
) {
  const today = new Date();
  const todayFormatted = getFullDate(today);
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("isFeaturedPromotion", "==", true),
    where("datesFeaturedFor", "array-contains", todayFormatted),
    where("industry", "==", industry),
    where("type", "==", jobType)
  );
}

export function fetchSimilarSponsoredJobsFromFirestore(
  countryCode,
  areaLevelOne,
  industry,
  jobType
) {
  const today = new Date();
  const todayFormatted = getFullDate(today);
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today),
    where("location.countryCode", "==", countryCode),
    where("location.areaLevelOne", "==", areaLevelOne),
    where("isSponsoredPromotion", "==", true),
    where("datesSponsoredFor", "array-contains", todayFormatted),
    where("industry", "==", industry),
    where("type", "==", jobType)
  );
}

export function listenToEmployerPaidJobsFromFirestore(companyId) {
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("companyId", "==", companyId),
    where("isPaidFor", "==", true),
    where("dateCreated", "!=", ""),
    orderBy("dateCreated", "desc")
  );
}

export function listenToEmployerFreeJobsFromFirestore(companyId) {
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("companyId", "==", companyId),
    where("isFree", "==", true),
    where("dateCreated", "!=", ""),
    orderBy("dateCreated", "desc")
  );
}

export function listenToCompanyJobsFromFirestore(refName) {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("companyRefName", "==", refName),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today)
  );
}

export function listenEmployerPaidCompanyFeaturedLogos(companyId) {
  return query(
    collection(db, FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION),
    where("companyId", "==", companyId),
    where("isPaidFor", "==", true),
    orderBy("datePurchased", "desc")
  );
}

export function listenEmployerFreeCompanyFeaturedLogos(companyId) {
  return query(
    collection(db, FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION),
    where("companyId", "==", companyId),
    where("isFree", "==", true),
    orderBy("datePurchased", "desc")
  );
}

export function listenToAdminUnderReviewItems({
  dbCollection,
  field = "isPaidFor",
}) {
  // FIRESTORE_JOBS_COLLECTION
  // listenToAdminUnderReviewPaidJobsFromFirestore isPaidFor
  // listenToAdminUnderReviewFreeJobsFromFirestore isFree
  // FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION
  // listenToAdminPaidFeaturedCompanyLogosFromFirestore isPaidFor
  // listenToAdminFreeFeaturedCompanyLogosFromFirestore isFree
  // FIRESTORE_COMPANY_PROFILE_COLLECTION
  // listenToAdminCompanyProfilesFromFirestore isPaidFor
  return query(
    collection(db, dbCollection),
    where("employerId", "!=", ""),
    where("isApproved", "==", false),
    where("isClosed", "==", false),
    where(field, "==", true)
  );
}
export function listenToAdminExpiredJobsFromFirestore() {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("dateExpiring", "<", today),
    where("isClosed", "==", false)
  );
}

export function listenToAdminPromotionRequestsFromFirestore() {
  return query(
    collection(db, FIRESTORE_PROMOTION_REQUESTS_COLLECTION),
    where("isFulfilled", "==", false),
    where("isPaidFor", "==", true),
    orderBy("dateCreated", "desc")
  );
}

export function listenToAdminRequestDeleteAccounts() {
  return query(
    collection(db, FIRESTORE_REQUEST_DELETE_ACCOUNTS_COLLECTION),
    where("email", "!=", "")
  );
}

export function listenToEmployerDraftJobsFromFirestore(companyId) {
  return query(
    collection(db, FIRESTORE_DRAFT_JOBS_COLLECTION),
    where("companyId", "==", companyId),
    where("dateCreated", "!=", ""),
    orderBy("dateCreated", "desc")
  );
}

export function listenToJobSeekerSavedJobsFromFirestore(jobSeekerId) {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_SAVED_JOBS_COLLECTION),
    where("jobSeekerId", "==", jobSeekerId),
    where("dateExpiring", ">=", today)
  );
}

export async function deletePersonalDataFromJobInFirestore(job, email) {
  const dateToday = new Date();
  const noPersonalDataJob = {
    ...job,
    isApproved: false,
    isActive: false,
    employerId: "",
    contactEmail: "",
    contactName: "",
    location: {},
    phoneNumber: "",
    isClosed: true,
    dateDeleted: dateToday,
    dateMonthYearDeleted: getMonthYearDate(dateToday),
    dateIsoWeekDeleted: getIsoWeekDate(dateToday),
    dateFullDeleted: getFullDate(dateToday),
    description: "",
    keywords: [],
    multipleLocations: [],
    isDeleted: true,
  };
  await updateDoc(
    doc(db, FIRESTORE_JOBS_COLLECTION, job.id),
    noPersonalDataJob
  );
  await checkToUpdateMultiLocJobs({ job, email });
}

export function setJobSeekerUserProfileData({
  user,
  firstName,
  lastName,
  countryCode = "CY",
  isReceiveMarketing = false,
}) {
  const today = new Date();
  return setDoc(doc(db, FIRESTORE_USERS_COLLECTION, user.uid), {
    displayName: user.displayName,
    firstName: firstName,
    lastName: lastName,
    email: user.email,
    phoneNumber: "",
    photoURL: user.photoURL || null,
    createdAt: today,
    dateMonthYear: getMonthYearDate(today),
    dateIsoWeek: getIsoWeekDate(today),
    isJobSeeker: true,
    isEmployer: false,
    countryCode: countryCode,
    type: JOB_SEEKER_USER,
    isAccountVerified: false,
    sibId: null,
    isSignupWithMarketing: isReceiveMarketing,
    candidateProfileId: null,
  });
}

export function setEmployerUserProfileData({
  user,
  company,
  firstName,
  lastName,
  phoneNumber,
  countryCode,
  currencyCode,
  isRecruiter = false,
  isReceiveMarketing = false,
  mode = HR_ALL_MODE,
}) {
  const today = new Date();
  return setDoc(doc(db, FIRESTORE_USERS_COLLECTION, user.uid), {
    displayName: user.displayName,
    firstName: firstName,
    lastName: lastName,
    company: company,
    email: user.email,
    phoneNumber: phoneNumber,
    billingAddress: null,
    photoURL: user.photoURL || null,
    createdAt: today,
    dateMonthYear: getMonthYearDate(today),
    dateIsoWeek: getIsoWeekDate(today),
    isEmployer: true,
    isJobSeeker: false,
    isAllowHideLocation: false,
    countryCode: countryCode,
    currencyCode: currencyCode,
    type: EMPLOYER_USER,
    isAccountVerified: false,
    sibId: null,
    isRecruiter: isRecruiter,
    isSignupWithMarketing: isReceiveMarketing,
    companyProfileId: null,
    mode: mode,
  });
}

export function addJobToFirestore({
  job,
  employerId,
  jobRefNumber,
  dateToday,
  isFree = false,
  isPaidFor = false,
  isPostedByRecruiter = false,
  companyRefName = "",
}) {
  return addDoc(collection(db, FIRESTORE_JOBS_COLLECTION), {
    ...job,
    employerId: employerId,
    isApproved: false,
    isActive: true,
    isClosed: false,
    dateCreated: dateToday,
    dateExpiring: setExpiryDate({ duration: 29 }), // admin will overwrite this
    keywords: [],
    adminKeywords: [],
    refNumber: jobRefNumber,
    isInitialised: false,
    isPaidFor: isPaidFor,
    isFree: isFree,
    isPostedByRecruiter: isPostedByRecruiter,
    companyRefName: companyRefName,
  });
}

export function addJobPostOrderToFirestore({
  jobRefNumber,
  jobId,
  employerId,
  additionalData,
  servicesBreakdown,
  dateSubmitted,
  countryCode,
  areaLevelOne,
  paymentIntentId = "",
  invoiceId = "",
  invoiceUrl = "",
  isFree = false,
  isDiscounted = false,
  discount = null,
  isPostedByRecruiter = false,
  isLocationHidden = false,
  isFromSubscription = false,
  isMultipleLocations,
}) {
  return setDoc(doc(db, FIRESTORE_JOB_POST_ORDER_COLLECTION, jobRefNumber), {
    refNumber: jobRefNumber,
    jobId: jobId,
    dateSubmitted: dateSubmitted,
    employerId: employerId,
    servicesBreakdown: servicesBreakdown,
    ...additionalData,
    countryCode: countryCode,
    areaLevelOne: areaLevelOne,
    paymentIntentId: paymentIntentId,
    invoiceId: invoiceId,
    invoiceUrl: invoiceUrl,
    isPublished: false,
    isFree: isFree,
    isDiscounted: isDiscounted,
    discount: discount,
    isPostedByRecruiter: isPostedByRecruiter,
    isLocationHidden: isLocationHidden,
    isFromSubscription: isFromSubscription,
    isMultipleLocations,
  });
}

export function updateViewCountForJobInFirestore({ jobRefNumber, viewerId }) {
  const dateToday = new Date();
  return updateDoc(doc(db, FIRESTORE_VIEWS_COLLECTION, jobRefNumber), {
    dateLastViewed: dateToday,
    views: arrayUnion({
      viewerId: viewerId,
      dateViewed: dateToday,
    }),
    viewsCount: increment(1),
  });
}

export function updateStatusLogForServiceInFirestore({
  collectionName = FIRESTORE_JOBS_STATUS_LOG_COLLECTION,
  id,
  date,
  status,
}) {
  return updateDoc(doc(db, collectionName, id), {
    timestamp: serverTimestamp(),
    log: arrayUnion({
      date: date,
      status: status,
    }),
  });
}

export function listenToDocFromFirestore({ dbCollection, id }) {
  console.log("listenToDocFromFirestore", dbCollection, id);
  return doc(db, dbCollection, id);
}

export function addDocToFirestore({ dbCollection, data }) {
  return addDoc(collection(db, dbCollection), { ...data });
}

export function updateFieldOnDoc({ dbCollection, id, key, value }) {
  return updateDoc(doc(db, dbCollection, id), {
    [key]: value,
  });
}

export function updateDocument({ dbCollection, id, data }) {
  return updateDoc(doc(db, dbCollection, id), {
    ...data,
  });
}

export function toggleSettingInDocument({ dbCollection, id, key, toggle }) {
  return updateDoc(doc(db, dbCollection, id), {
    [key]: !toggle,
  });
}

export function incrementValueInDocument({
  dbCollection,
  id,
  key,
  incrementBy = 1,
}) {
  return updateDoc(doc(db, dbCollection, id), {
    [key]: increment(incrementBy),
  });
}

export function updateJobInFirestore({ job, isAdmin = false }) {
  // keywords are set when job is posted or updated from employer
  // admins add keywords manually
  const approvedStatus = isAdmin ? job.isApproved : false;
  return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, job.id), {
    ...job,
    isApproved: approvedStatus,
    isActive: true,
  });
}

export function setJobActiveInFirestore(jobId, isActive) {
  return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, jobId), {
    isActive: isActive,
  });
}

export function setJobLocationDetails(job, values) {
  return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, job.id), {
    location: {
      address: values.address,
      latLng: {
        lat: parseFloat(values.latLng.lat),
        lng: parseFloat(values.latLng.lng),
      },
      locality: values.locality,
      areaLevelOne: values.areaLevelOne,
      countryCode: values.countryCode,
      country: values.country,
    },
  });
}

export function setJobListedLocationDetails(job, values) {
  return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, job.id), {
    listedLocation: {
      address: values.address,
      city: values.city,
      country: values.country,
      countryCode: values.countryCode,
    },
  });
}

export function promoteJob({ job, days, promotionType }) {
  const datesPromoted = getAdditionalPromotedDates({
    publishedDate: new Date(),
    promotionDuration: days,
  });
  const datePromotedExpiring = datesPromoted.expiring;
  const datesPromotedFor = datesPromoted.datesFor;
  const dateExpiring = isAfter(datePromotedExpiring, job.dateExpiring)
    ? datePromotedExpiring
    : job.dateExpiring;
  if (promotionType === "featured") {
    return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, job.id), {
      isFeaturedPromotion: true,
      dateFeaturedExpiring: datePromotedExpiring,
      datesFeaturedFor: datesPromotedFor,
      dateExpiring: dateExpiring,
    });
  } else if (promotionType === "sponsored") {
    return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, job.id), {
      isSponsoredPromotion: true,
      dateSponsoredExpiring: datePromotedExpiring,
      datesSponsoredFor: datesPromotedFor,
      dateExpiring: dateExpiring,
    });
  }
}

export function addSavedLocationToEmployer({ employerId, savedLocation }) {
  return setDoc(
    doc(
      db,
      FIRESTORE_USERS_COLLECTION,
      employerId,
      FIRESTORE_SAVED_LOCATIONS_SUB_COLLECTION,
      cuid()
    ),
    savedLocation
  );
}

export function fetchEmployerSavedLocations(employerId) {
  return collection(
    db,
    FIRESTORE_USERS_COLLECTION,
    employerId,
    FIRESTORE_SAVED_LOCATIONS_SUB_COLLECTION
  );
}

export function setAdminKeywords(job, adminKeywords) {
  return updateDoc(doc(db, FIRESTORE_JOBS_COLLECTION, job.id), {
    keywords: [...job.keywords, adminKeywords],
    adminKeywords: adminKeywords,
  });
}

export function addDraftJobToFirestore(job, employerId) {
  const dateToday = new Date();
  return addDoc(collection(db, FIRESTORE_DRAFT_JOBS_COLLECTION), {
    ...job,
    employerId: employerId,
    isApproved: false,
    dateCreated: dateToday,
    dateExpiring: "",
    location: EMPTY_LOCATION,
  });
}

export function addSavedJobToFirestore(job, jobSeekerId) {
  const dateToday = new Date();
  return addDoc(collection(db, FIRESTORE_SAVED_JOBS_COLLECTION), {
    jobId: job.id,
    refNumber: job.refNumber,
    employerId: job.employerId,
    jobSeekerId: jobSeekerId,
    title: job.title,
    company: job.company,
    type: job.type,
    industry: job.industry,
    salary: job.salary,
    companyLogoPhotoURL: job.companyLogoPhotoURL,
    datePublished: job.datePublished,
    dateExpiring: job.dateExpiring,
    dateSaved: dateToday,
  });
}

export function fetchUserDataFromFirestore(email) {
  return query(
    collection(db, FIRESTORE_USERS_COLLECTION),
    where("email", "==", email)
  );
}

export function fetchJobUsingRefFromFirestore(ref) {
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("refNumber", "==", ref)
  );
}

export function listenToJobUsingRefFromFirestore(ref) {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("refNumber", "==", ref),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today)
  );
}

export function getMainLocationJob(ref) {
  return query(
    collection(db, FIRESTORE_JOBS_COLLECTION),
    where("refNumber", "==", ref),
    where("employerId", "!=", MULTIPLE_LOCATION_JOB_EMPLOYER_ID)
  );
}

export function deleteDocFromFirestore({ dbCollection, id }) {
  return deleteDoc(doc(db, dbCollection, id));
}

export function setRequestDeleteAccount(userId, email) {
  const dateToday = new Date();
  return setDoc(doc(db, FIRESTORE_REQUEST_DELETE_ACCOUNTS_COLLECTION, userId), {
    userId: userId,
    email: email,
    dateRequestedDelete: dateToday,
    isAuthDeleted: false,
  });
}

export function requestDeleteAccount(userId) {
  return updateDoc(
    doc(db, FIRESTORE_REQUEST_DELETE_ACCOUNTS_COLLECTION, userId),
    {
      userId: userId,
      isAuthDeleted: true,
    }
  );
}

export function logErrorInFirestore({
  error = "error string",
  errorPage = "",
  firestoreListener = false,
  errorCode = 0,
  isMobile = false,
  isAdminLog = false,
  notes = {},
}) {
  if (!isAdminLog) {
    console.log("error will be logged");
  }
  const user = auth.currentUser;
  const today = new Date();
  const errorId = format(today, "yyyyMMdd-HH:mm");
  return setDoc(doc(db, FIRESTORE_ERRORS_COLLECTION, errorId + `-${cuid()}`), {
    date: today,
    dateIsoWeek: getIsoWeekDate(today),
    dateFull: getFullDate(today),
    error: error,
    errorPage: errorPage,
    userId: user?.uid || null,
    firestoreListener: firestoreListener,
    errorCode: errorCode,
    isMobile: isMobile,
    isChecked: false,
    ...notes,
  });
}

export function logCookieConsent({ userId, cookieConsent }) {
  const today = new Date();
  return addDoc(collection(db, FIRESTORE_COOKIE_CONSENT_RECORD_COLLECTION), {
    date: today,
    dateFull: getFullDate(today),
    dateMonthYear: getMonthYearDate(today),
    dateIsoWeek: getIsoWeekDate(today),
    cookieConsent: cookieConsent,
    userId: userId,
  });
}

export function addFeaturedCompanyLogoToFirestore({
  countryCode,
  currencyCode,
  refNumber,
  companyRefName = "",
  employerId,
  featuredCompanyLogoURL,
  price,
  dateToday,
  isFree = false,
}) {
  return addDoc(collection(db, FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION), {
    datePurchased: dateToday,
    isInitialised: false,
    isApproved: false,
    isActive: false,
    isClosed: false,
    countryCode: countryCode,
    currencyCode: currencyCode,
    price: price,
    employerId: employerId,
    featuredCompanyLogoURL: featuredCompanyLogoURL,
    refNumber: refNumber,
    isPaidFor: false,
    isFree: isFree,
    companyRefName: companyRefName,
  });
}

export function updateFeaturedCompanyLogoWithPaymentDetails({
  logoId,
  paymentIntentId,
  invoiceId,
  invoiceUrl,
}) {
  return updateDoc(
    doc(db, FIRESTORE_FEATURED_COMPANY_LOGOS_COLLECTION, logoId),
    {
      paymentIntentId: paymentIntentId,
      invoiceId: invoiceId,
      invoiceUrl: invoiceUrl,
    }
  );
}

export function addCompanyProfileToFirestore({
  profile,
  employerId,
  countryCode,
  refName,
  dateToday = new Date(),
  isFree = false,
  isPaidFor = false,
  isPostedByRecruiter = false,
  isFromSubscription = false,
  mode = HR_ALL_MODE,
}) {
  return addDoc(collection(db, FIRESTORE_COMPANY_PROFILE_COLLECTION), {
    ...profile,
    firstLetter: profile.name.charAt(0),
    countryCode: countryCode,
    nameKeywords: generateKeywordsFromString(profile.name),
    employerId: employerId,
    isApproved: false,
    isActive: true,
    dateCreated: dateToday,
    dateSubmitted: dateToday,
    dateExpiring: setExpiryDate({ duration: 365 }), // admin will overwrite this
    refName: refName,
    isInitialised: false,
    isPaidFor: isPaidFor,
    isFree: isFree,
    isClosed: false,
    isPostedByRecruiter: isPostedByRecruiter,
    views: 0,
    isFromSubscription: isFromSubscription,
    mode,
  });
}

export function updateCompanyProfileInFirestore({ company, isAdmin = false }) {
  const approvedStatus = isAdmin ? company.isApproved : false;
  return updateDoc(doc(db, FIRESTORE_COMPANY_PROFILE_COLLECTION, company.id), {
    ...company,
    isApproved: approvedStatus,
    isActive: true,
  });
}

export function listenToCompanyProfilesFromFirestore(countryCode) {
  return query(
    collection(db, FIRESTORE_COMPANY_PROFILE_COLLECTION),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("countryCode", "==", countryCode),
    orderBy("name", "asc")
  );
}

export function setCompanyLocationDetails(company, values) {
  const updatedLocation = {
    address: values.address,
    latLng: {
      lat: parseFloat(values.latLng.lat),
      lng: parseFloat(values.latLng.lng),
    },
    locality: values.locality,
    areaLevelOne: values.areaLevelOne,
    countryCode: values.countryCode,
  };
  return updateDoc(doc(db, FIRESTORE_COMPANY_PROFILE_COLLECTION, company.id), {
    location: updatedLocation,
  });
}

export function fetchCompanyUsingRefFromFirestore(ref) {
  return query(
    collection(db, FIRESTORE_COMPANY_PROFILE_COLLECTION),
    where("refName", "==", ref)
  );
}

export function listenToCompanyUsingRefFromFirestore(ref) {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_COMPANY_PROFILE_COLLECTION),
    where("refName", "==", ref),
    where("isApproved", "==", true),
    where("isActive", "==", true),
    where("dateExpiring", ">=", today)
  );
}

export function addCandidateProfileToFirestore({
  profile,
  jobSeekerId,
  countryCode,
  ref,
  dateToday = new Date(),
}) {
  return addDoc(collection(db, FIRESTORE_CANDIDATE_PROFILES_COLLECTION), {
    ...profile,
    isOpenToWork:
      profile.status === ASAP_JOB || profile.status === SIX_MONTHS_JOB,
    dateFull: getFullDate(dateToday),
    dateMonthYear: getMonthYearDate(dateToday),
    dateIsoWeek: getIsoWeekDate(dateToday),
    countryCode: countryCode,
    jobSeekerId: jobSeekerId,
    dateCreated: dateToday,
    dateLastEdited: dateToday,
    ref: ref,
    views: 0,
    isReviewed: false,
    adminKeywords: [],
  });
}

export function addCandidateProfileMetricsToFirestore({
  ref,
  candidateProfileId,
  jobSeekerId,
  countryCode,
  dateToday = new Date(),
}) {
  return setDoc(doc(db, FIRESTORE_CANDIDATE_PROFILES_METRICS_COLLECTION, ref), {
    ref,
    candidateProfileId: candidateProfileId,
    jobSeekerId: jobSeekerId,
    countryCode: countryCode,
    dateCreated: dateToday,
    dateFull: getFullDate(dateToday),
    dateMonthYear: getMonthYearDate(dateToday),
    dateIsoWeek: getIsoWeekDate(dateToday),
    views: 0,
    linkedInViews: 0,
    cvViews: 0,
    searchAppearances: 0,
  });
}

export function updateCandidateProfileOnFirestore({
  profile,
  profileId,
  dateToday = new Date(),
}) {
  return updateDoc(
    doc(db, FIRESTORE_CANDIDATE_PROFILES_COLLECTION, profileId),
    {
      ...profile,
      isOpenToWork:
        profile.status === ASAP_JOB || profile.status === SIX_MONTHS_JOB,
      dateLastEdited: dateToday,
      isReviewed: false,
    }
  );
}

export function addApplicationToFirestore({
  coverLetter = null,
  jobId,
  jobTitle,
  jobRefNumber,
  employerId,
  jobSeekerId,
  candidateProfileId,
  candidateProfileRef,
  countryCode,
  dateToday = new Date(),
  dateJobExpiring,
  jobLocationAddress,
}) {
  return addDoc(collection(db, FIRESTORE_APPLICATIONS_COLLECTION), {
    jobId,
    jobTitle,
    jobRefNumber,
    employerId,
    jobSeekerId,
    candidateProfileId,
    candidateProfileRef,
    dateFull: getFullDate(dateToday),
    dateMonthYear: getMonthYearDate(dateToday),
    dateIsoWeek: getIsoWeekDate(dateToday),
    dateApplied: dateToday,
    dateJobExpiring,
    countryCode,
    isNotified: false,
    isContactedByEmployer: false,
    isDeletedByEmployer: false,
    coverLetter: coverLetter,
    jobLocationAddress,
  });
}

export function listenToJobSeekerApplications(jobSeekerId) {
  const today = new Date();
  return query(
    collection(db, FIRESTORE_APPLICATIONS_COLLECTION),
    where("jobSeekerId", "==", jobSeekerId),
    where("dateJobExpiring", ">=", today),
    orderBy("dateJobExpiring", "desc")
  );
}

export function listenToJobApplications({ user, userId, jobId }) {
  return query(
    collection(db, FIRESTORE_APPLICATIONS_COLLECTION),
    where(user, "==", userId),
    where("jobId", "==", jobId)
  );
}

export function addJobAlertPreferencesToFirestore({
  jobSeekerId,
  jobKeywords,
  industries,
  locations,
  email,
  displayName,
  countryCode,
  dateToday = new Date(),
  type = CUSTOM_ALERT_TYPE,
  frequency = WEEKLY_ALERT_FREQUENCY,
}) {
  return addDoc(collection(db, FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION), {
    jobKeywords,
    industries,
    locations,
    isActive: true,
    email,
    displayName,
    countryCode,
    jobSeekerId,
    dateCreated: dateToday,
    dateLastEdited: dateToday,
    dateFull: getFullDate(dateToday),
    dateIsoWeek: getIsoWeekDate(dateToday),
    dateMonthYear: getMonthYearDate(dateToday),
    type,
    frequency,
  });
}

export function updateJobAlertPreferencesOnFirestore({
  jobKeywords,
  industries,
  locations,
  preferencesId,
  dateToday = new Date(),
}) {
  return updateDoc(
    doc(db, FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION, preferencesId),
    {
      jobKeywords,
      industries,
      locations,
      isActive: true,
      dateLastEdited: dateToday,
    }
  );
}

export function listenToJobSeekerJobAlertPreferencesFromFirestore(jobSeekerId) {
  return query(
    collection(db, FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION),
    where("jobSeekerId", "==", jobSeekerId)
  );
}

/////////////////////// ADMIN;
export function deleteFieldFromDocument({ dbCollection, id, field }) {
  return updateDoc(doc(db, dbCollection, id), {
    [field]: deleteField(),
  });
}

export function addDocWithId({ dbCollection, id, data }) {
  return setDoc(doc(db, dbCollection, id), data);
}

// export function fetchCollection() {
//   return getDocs(collection(db, FIRESTORE_JOB_POST_ORDER_COLLECTION));
// }

export function addCouponInFirestore({
  couponId,
  couponNameId,
  name,
  employerId,
  dateToday = new Date(),
  dateMonthYear = getMonthYearDate(dateToday),
  dateIsoWeek = getIsoWeekDate(dateToday),
  dateFull = getFullDate(dateToday),
  title = "",
  countryCode,
}) {
  console.log(">> addCouponInFirestore");
  return addDoc(collection(db, FIRESTORE_COUPONS_COLLECTION), {
    isUsed: false,
    employerId: employerId,
    couponId: couponId,
    couponNameId: couponNameId,
    name: name,
    dateIssued: dateToday,
    dateMonthYear: dateMonthYear,
    dateIsoWeek: dateIsoWeek,
    dateFull: dateFull,
    title: title,
    countryCode: countryCode,
  });
}
